import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { documentKeys, documentListQuery, reasonListQuery } from "@/features/documents/api";
import { AdjustmentPanel } from "@/features/documents/AdjustmentPanel";
import { DocumentsPage } from "@/features/documents/DocumentsPage";
import { documentListColumns, documentsSearchValidator, type DocumentsSearch } from "@/features/documents/search";
import { ImportPanel } from "@/features/import/ImportPanel";
import { stockKeys } from "@/features/inventory/api";
import { warehouseListQuery } from "@/features/warehousing/api";
import { useActiveCompany } from "@/lib/activeCompany";
import { useCan } from "@/lib/session";
import { viewQuery } from "@/lib/listView";

// Stock adjustments: list (keyset on created_at, id; status filter) → document
// panel (stock_document RPC) with post / cancel / reverse. Import: opening
// stock from Excel into the active company (posts OPENING / CORRECTION
// adjustments, docs/design/import.md).

const invalidateAfterImport = [documentKeys.all, stockKeys.all] as const;

export const Route = createFileRoute("/_app/warehousing/adjustments")({
  validateSearch: documentsSearchValidator(documentListColumns.stock_adjustment),
  loaderDeps: ({ search }) => ({ status: search.status, f: search.f, s: search.s }),
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(reasonListQuery);
    void context.queryClient.prefetchInfiniteQuery(warehouseListQuery(false));
    return context.queryClient.ensureInfiniteQueryData(documentListQuery("stock_adjustment", deps.status, viewQuery(documentListColumns.stock_adjustment, deps)));
  },
  component: AdjustmentsPage,
});

function AdjustmentsPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const setSearch = useCallback(
    (patch: Partial<DocumentsSearch>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const canImport = useCan("core.data.import");
  const canPost = useCan("inventory.adjustment.post");
  const { company } = useActiveCompany();
  return (
    <DocumentsPage
      type="stock_adjustment"
      search={search}
      setSearch={setSearch}
      permission="inventory.adjustment.post"
      title={t("documents.adjustmentsTitle")}
      breadcrumb={t("nav.warehousing.adjustments")}
      newLabel={t("documents.newAdjustment")}
      onImport={canImport && canPost ? () => setSearch({ import: "opening_stock", doc: undefined }) : undefined}
      aside={
        search.import
          ? {
              label: t("import.title"),
              node: (
                <ImportPanel
                  entities={["opening_stock"]}
                  initial="opening_stock"
                  companyId={company?.id}
                  onClose={() => setSearch({ import: undefined })}
                  invalidate={invalidateAfterImport}
                />
              ),
            }
          : undefined
      }
      renderPanel={(docId) => (
        <AdjustmentPanel key={docId} docId={docId} onClose={() => setSearch({ doc: undefined })} onOpen={(id) => setSearch({ doc: id })} />
      )}
    />
  );
}
